/**
 * ConfirmationPrompt — Shows a Yes/No prompt near the avatar for page actions.
 * Uses DOM elements (not PixiJS) for reliable click handling.
 */

const AUTO_DISMISS_MS = 10000

export class ConfirmationPrompt {
  private container: HTMLDivElement | null = null

  /**
   * Ask the visitor to confirm an action. Returns true if confirmed, false if declined.
   * Auto-declines after 10 seconds.
   */
  ask(description: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      // Remove any existing prompt
      this.dismiss()

      this.container = document.createElement('div')
      Object.assign(this.container.style, {
        position: 'fixed',
        bottom: '300px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '2147483647',
        background: 'rgba(255, 255, 255, 0.97)',
        border: '1.5px solid #c8d0da',
        borderRadius: '16px',
        padding: '14px 18px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '13px',
        color: '#2a2a3e',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        maxWidth: '280px',
        textAlign: 'center',
        animation: 'hs-confirm-in 0.25s ease-out',
      })

      // Inject keyframes
      this.ensureKeyframes()

      this.container.setAttribute('role', 'alertdialog')
      this.container.setAttribute('aria-label', 'Confirm action')

      // Description text
      const text = document.createElement('div')
      text.textContent = description
      text.style.lineHeight = '1.4'
      this.container.appendChild(text)

      // Button row
      const btnRow = document.createElement('div')
      Object.assign(btnRow.style, {
        display: 'flex',
        gap: '8px',
        width: '100%',
      })

      const yesBtn = this.createButton('Yes', '#4a6fa5', '#ffffff')
      const noBtn = this.createButton('No', '#e8ecf0', '#2a2a3e')

      btnRow.appendChild(yesBtn)
      btnRow.appendChild(noBtn)
      this.container.appendChild(btnRow)

      document.body.appendChild(this.container)

      let resolved = false

      const done = (result: boolean) => {
        if (resolved) return
        resolved = true
        clearTimeout(timer)
        this.dismiss()
        resolve(result)
      }

      yesBtn.addEventListener('click', () => done(true))
      noBtn.addEventListener('click', () => done(false))

      // Auto-decline after timeout
      const timer = setTimeout(() => done(false), AUTO_DISMISS_MS)
    })
  }

  /** Dismiss any active prompt. */
  dismiss(): void {
    if (this.container) {
      this.container.remove()
      this.container = null
    }
  }

  private createButton(label: string, bg: string, fg: string): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.textContent = label
    Object.assign(btn.style, {
      flex: '1',
      padding: '8px 0',
      border: 'none',
      borderRadius: '10px',
      background: bg,
      color: fg,
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'opacity 0.15s',
    })
    btn.addEventListener('mouseenter', () => { btn.style.opacity = '0.85' })
    btn.addEventListener('mouseleave', () => { btn.style.opacity = '1' })
    return btn
  }

  private ensureKeyframes(): void {
    if (document.getElementById('hs-confirm-keyframes')) return
    const style = document.createElement('style')
    style.id = 'hs-confirm-keyframes'
    style.textContent = `
      @keyframes hs-confirm-in {
        from { opacity: 0; transform: translateX(-50%) translateY(8px); }
        to { opacity: 1; transform: translateX(-50%) translateY(0); }
      }
    `
    document.head.appendChild(style)
  }
}
